import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Control, FieldValues, Path, RegisterOptions } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CustomSelect, { MenuData } from "../input/CustomSelect";
import { CustomAutoComplete } from "../input/CustomAutoComplete";
import { common, financedEmissions } from "../../i18n";
import { SectorOption } from "../../models/dto/cat15/cat15.api";
import { fetchSectors } from "../../service/cat15.service";

interface Props<T extends FieldValues> {
  /** form field holding the ISIC code sent to the BE */
  name: Path<T>;
  control: Control<T>;
  /** current value of `name`, watched by the parent form */
  value: string | null;
  /** writes `name` when a section is picked (clears it) or has no codes (the section itself) */
  onChange: (code: string) => void;
  error?: boolean;
  errorMessage?: string;
  /** the section field's label; the bank's wording unless a host words it its own way */
  label?: string;
  /** marks both fields required; the bank always asks for a sector */
  required?: boolean;
  /** where the sections and their codes come from; the bank's own list unless a host passes another */
  loadSectors?: (parentId?: string) => Promise<SectorOption[]>;
  /** form rules for the answer, for a host that gates on the form being valid */
  rules?: Pick<RegisterOptions<T, Path<T>>, "required">;
  /** shown when the lists cannot be loaded; without it a failed load just leaves them empty */
  unavailableMessage?: string;
}

// rejects on a failed request, so an outage is never read as a section with no codes
const loadBankSectors = (parentId?: string) => (parentId ? fetchSectors("isic", parentId) : fetchSectors("isic"));

// code first: the autocomplete searches it, and bankers look industries up by BOT code or ISIC letter
const toCodeMenuData = (s: SectorOption): MenuData => ({
  value: s.value,
  nameEN: `${s.value} — ${s.labelEn}`,
  nameTH: `${s.value} — ${s.labelTh}`,
});

// Section, then its searchable BOT code; a section with no BOT codes is saved as the section itself.
const SectorIsicPicker = <T extends FieldValues>({
  name,
  control,
  value,
  onChange,
  error,
  errorMessage,
  label,
  required = true,
  loadSectors = loadBankSectors,
  rules,
  unavailableMessage,
}: Props<T>) => {
  const { t, i18n } = useTranslation([financedEmissions, common], { nsMode: "fallback" });
  const [sections, setSections] = useState<SectorOption[]>([]);
  // null = follow the form value, so a prefill that lands after mount still opens its section
  const [pickedSection, setPickedSection] = useState<string | null>(null);
  const [fetched, setFetched] = useState<{ sectionId: string; rows: SectorOption[] } | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let active = true;
    loadSectors()
      .then((rows) => active && setSections(rows))
      .catch(() => active && setUnavailable(true));
    return () => {
      active = false;
    };
  }, [loadSectors]);

  // a BOT code starts with its section letter
  const section =
    pickedSection ?? (value ? (sections.some((s) => s.value === value) ? value : value.charAt(0)) : "");
  const sectionId = sections.find((s) => s.value === section)?.id;
  // the previous section's list survives one render after a switch; reading it would save a code-less answer for the new section
  const codes = fetched && fetched.sectionId === sectionId ? fetched.rows : null;

  useEffect(() => {
    if (!sectionId) return;
    let active = true;
    loadSectors(sectionId)
      .then((rows) => active && setFetched({ sectionId, rows }))
      .catch(() => active && setUnavailable(true));
    return () => {
      active = false;
    };
  }, [sectionId, loadSectors]);

  useEffect(() => {
    if (codes && codes.length === 0 && section && value !== section) onChange(section);
  }, [codes, section, value, onChange]);

  const sectionMenu = useMemo(() => sections.map(toCodeMenuData), [sections]);
  const codeMenu = useMemo(() => (codes ?? []).map(toCodeMenuData), [codes]);
  const codeByValue = useMemo(() => new Map((codes ?? []).map((c) => [c.value, c])), [codes]);
  const sectionByValue = useMemo(() => new Map(sections.map((s) => [s.value, s])), [sections]);
  // a monospaced code column lines every name up, even after one-letter codes like a narrow "I" and a wide "M"
  const renderCodeRow = (byValue: Map<string, SectorOption>) => (option: MenuData) => {
    const sector = byValue.get(option.value);
    return (
      <Box component="span" display="flex" gap="8px">
        <Box component="span" sx={{ fontFamily: "monospace", flexShrink: 0 }}>
          {option.value}
        </Box>
        <Box component="span" flexShrink={0}>
          —
        </Box>
        <Box component="span">{i18n.language.includes("th") ? sector?.labelTh : sector?.labelEn}</Box>
      </Box>
    );
  };
  const hasCodes = codeMenu.length > 0;

  // the code field's unmount takes the rule with it, so it is put back whenever that field comes or goes
  useEffect(() => {
    if (rules) control.register(name, rules);
  }, [control, name, rules, hasCodes]);

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <CustomSelect
        required={required}
        label={label ?? t("Sector-ISIC-Primary")}
        value={section}
        listMenuItem={sectionMenu}
        renderOptionLabel={renderCodeRow(sectionByValue)}
        error={!!error && !section}
        errorMessage={errorMessage}
        handleValeOnChange={(e) => {
          setPickedSection(e.target.value);
          onChange("");
        }}
      />
      {hasCodes && (
        <CustomAutoComplete
          required={required}
          name={name}
          control={control}
          label={t("Sector-ISIC-Code")}
          info=""
          placeholder={t("Sector-ISIC-Code-Placeholder")}
          listMenuItem={codeMenu}
          renderOptionLabel={renderCodeRow(codeByValue)}
          rules={rules}
        />
      )}
      {codes && !hasCodes && section && (
        <Typography variant="body2" color="var(--gray9)">
          {t("Sector-ISIC-No-Codes")}
        </Typography>
      )}
      {unavailable && unavailableMessage && (
        <Typography variant="body2" color="var(--gray9)">
          {unavailableMessage}
        </Typography>
      )}
      {/* with rules the code field prints the form's own error */}
      {error && !rules && hasCodes && !value && (
        <Typography variant="body2" color="var(--red)">
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default SectorIsicPicker;
